import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useExternalProviderRepository } from "../context/external-provider-repository.context";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { useApiAction } from "@shared-kernel/primary/shared/api-errors/use-api-action";
import { applyApiFieldErrors } from "@shared-kernel/primary/shared/api-errors/apply-api-field-errors";
import { useFieldUpload } from "@shared-kernel/primary/shared/api-errors/use-field-upload";
import type { ActionForm } from "@shared-kernel/primary/shared/api-errors/action-form";
import {
  resetUploadExternalProviderInvoice,
  uploadExternalProviderInvoice,
} from "@academy-context/write/application/use-cases/external-provider/invoice-upload/upload-external-provider-invoice";
import { useResetWhenSettled } from "@shared-kernel/primary/shared/request-state/use-reset-when-settled";
import {
  submitExternalProviderInvoice,
  resetSubmitExternalProviderInvoice,
} from "@academy-context/write/application/use-cases/external-provider/invoice-submission/submit-external-provider-invoice";
import { selectExternalProviderInvoiceUpload } from "@academy-context/write/application/use-cases/external-provider/invoice-upload/selectors/external-provider-invoice-upload-selectors";
import { selectExternalProviderInvoiceSubmission } from "@academy-context/write/application/use-cases/external-provider/invoice-submission/selectors/external-provider-invoice-submission-selectors";
import { formatFormDataToBody, type InvoiceFormInputs } from "../form-validation/invoice-form.schema";

// Facture déposée gardée après succès (exception au reset automatique) : seul le refus remet l'état à zéro, le portail
// le remet à zéro en quittant l'écran
export const useInvoiceSubmit = (form: ActionForm<InvoiceFormInputs>, participationId: Nullable<string>) => {
  const dispatch = useAppDispatch();
  const run = useApiAction();
  const repository = useExternalProviderRepository();
  const upload = useFieldUpload(form);

  const { processing: uploadProcessing } = useAppSelector(selectExternalProviderInvoiceUpload);
  useResetWhenSettled(uploadProcessing, resetUploadExternalProviderInvoice);
  const { processing: submitProcessing } = useAppSelector(selectExternalProviderInvoiceSubmission);

  // L'API dit pourquoi le fichier est refusé (PDF invalide, trop volumineux). Sans feedback : le refus s'affiche sous le champ
  const uploadInvoice = useCallback(
    (file: File) => {
      void upload(
        "invoiceUrl",
        run(uploadExternalProviderInvoice({ file, externalProviderRepository: repository })),
        "Erreur lors du téléchargement de la facture",
      );
    },
    [upload, run, repository],
  );

  // L'API dit pourquoi la facture est refusée (contrat de sous-traitance pas encore signé, date antérieure à la période
  // comptable ouverte), sous le champ fautif quand elle le désigne. Le succès affiche la facture déposée
  const submitInvoice = useCallback(
    async (values: InvoiceFormInputs) => {
      if (!participationId) return;
      const outcome = await run(
        submitExternalProviderInvoice({ participationId, body: formatFormDataToBody(values), externalProviderRepository: repository }),
        {
          success: "Facture soumise avec succès !",
          failure: "Erreur lors de la soumission de la facture",
        },
      );
      if (!outcome.ok) {
        dispatch(resetSubmitExternalProviderInvoice());
        // Le body nomme l'IBAN providerIban ; le formulaire, iban
        applyApiFieldErrors(form, outcome.error, { providerIban: "iban" });
      }
    },
    [participationId, run, repository, dispatch, form],
  );

  return { uploadInvoice, submitInvoice, uploadProcessing, submitProcessing };
};
